import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { HubOption, HubOptionStateValue, HUB_MAX_REGULAR_OPTIONS, isContinueOption } from 'dorian-shared/types/branch/HubOption';
import type { HubOptionScore as HubOptionScoreType } from 'dorian-shared/types/branch/HubOption';
import { useMemoryIconsContext } from 'contexts/MemoryIconsContext/MemoryIconsContext';
import { useHubTagTemplatesContext } from 'contexts/HubTagTemplatesContext/HubTagTemplatesContext';
import { ConfirmModal } from 'components/ui/ConfirmModal/ConfirmModal';
import { CustomSelect } from '../../../../CustomSelect';
import { MemoryIconField } from '../../../../../Book/MemoryBank/MemoryFields/MemoryIconField';
import { OptionSectionRow } from '../shared/OptionSectionRow';
import { BranchSelectItem } from '../shared/types';
import './HubOptionCard.scss';
import { HubClearButton } from '../HubClearButton/HubClearButton';
import { IconOverrides } from './Icon/IconOverrides';
import { HubOptionReplay } from './Replay/HubOptionReplay';
import { HubOptionState } from './HubOptionState';
import { HubOptionScore } from './HubOptionScore';
import { TextDestOverrides } from './TextDest/TextDestOverrides';
import {
  HUB_CARD_TEXT_MAX_LENGTH,
  HUB_CARD_TEXT_WARN_LENGTH,
  HUB_CONTINUE_MAX_LENGTH,
} from '../hubStepDefaults';
import { TagsOverrides } from './Tags/TagsOverrides';
import { TagDefaultsPicker } from './Tags/TagDefaultsPicker';
import { HubOptionTagChip } from './Tags/HubOptionTagChip';
import { useTagConditions } from '../hooks/useTagConditions';
import { isTagConditionValid } from './Tags/tagConditionValidation';
import { BorderOverrides } from './Border/BorderOverrides';
import { BorderColorInput } from './Border/BorderColorInput';
import { useBorderConditions } from '../hooks/useBorderConditions';
import { isBorderConditionValid, isValidBorderHex } from '../borderConditionValidation';

type HubOptionCardProps = {
  option: HubOption;
  index: number;
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  isMaxReached: boolean;
  defaultExpanded?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (uuid: string) => void;
  onUpdate: (uuid: string, changes: Partial<HubOption>) => void;
  onDelete: (uuids: string[]) => void;
  onDuplicate: (uuid: string) => void;
};

function getBranchName(branchId: number | null, branches: BranchSelectItem[]): string {
  if (branchId == null) return '';
  if (branchId === -1) return '[Create New Node]';
  const branch = branches.find((b) => b.id === branchId);
  return branch ? branch.title : '';
}

const DEFAULT_OPTION_STATE = { state: HubOptionStateValue.Active } as const;

/** Normalize CustomSelect eventKey (string | null) to number | null. */
function parseGotoBranchId(raw: unknown): number | null {
  if (raw == null || raw === '' || raw === 'null') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

export function HubOptionCard({
  option,
  index,
  branches,
  currentBranchId,
  isDisabled,
  isMaxReached,
  defaultExpanded = false,
  isSelected = false,
  onToggleSelect,
  onUpdate,
  onDelete,
  onDuplicate,
}: HubOptionCardProps) {
  // Outer expand/collapse is a V1.0 compatibility seam — do not remove.
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTextDestOverridesOpen, setIsTextDestOverridesOpen] = useState(false);
  const [isIconOverridesOpen, setIsIconOverridesOpen] = useState(false);
  const [isTopTagsOpen, setIsTopTagsOpen] = useState(false);
  const [isBottomTagsOpen, setIsBottomTagsOpen] = useState(false);
  const [isBorderOpen, setIsBorderOpen] = useState(false);
  const isContinue = isContinueOption(option);
  const { memoryIcons, createMemoryIcon } = useMemoryIconsContext();
  const { templates: tagTemplates } = useHubTagTemplatesContext();
  const topTagConditions = useMemo(() => option.tagTopConditions ?? [], [option.tagTopConditions]);
  const bottomTagConditions = useMemo(() => option.tagBottomConditions ?? [], [option.tagBottomConditions]);

  const topDefaultTags = option.tags?.top ?? [];
  const bottomDefaultTags = option.tags?.bottom ?? [];

  // Tag overrides: dot visibility + validation (same pattern as Text & Dest / Icon)
  const hasTopTagOverrides = topTagConditions.length > 0;
  const topTagOverridesConfigured = useMemo(
    () => hasTopTagOverrides && topTagConditions.every(isTagConditionValid),
    [hasTopTagOverrides, topTagConditions],
  );

  const hasBottomTagOverrides = bottomTagConditions.length > 0;
  const bottomTagOverridesConfigured = useMemo(
    () => hasBottomTagOverrides && bottomTagConditions.every(isTagConditionValid),
    [hasBottomTagOverrides, bottomTagConditions],
  );

  const {
    addCondition: addTopTagOverride,
    removeCondition: removeTopTagOverride,
    updateCondition: updateTopTagOverride,
    handleDragEnd: handleTopTagDragEnd,
    summary: topTagSummary,
  } = useTagConditions(topTagConditions, 'top', (changes) => onUpdate(option.uuid, changes), tagTemplates);

  const {
    addCondition: addBottomTagOverride,
    removeCondition: removeBottomTagOverride,
    updateCondition: updateBottomTagOverride,
    handleDragEnd: handleBottomTagDragEnd,
    summary: bottomTagSummary,
  } = useTagConditions(bottomTagConditions, 'bottom', (changes) => onUpdate(option.uuid, changes), tagTemplates);

  const borderConditions = useMemo(() => option.borderConditions ?? [], [option.borderConditions]);
  const hasBorderOverrides = borderConditions.length > 0;
  const borderOverridesConfigured = useMemo(
    () => borderConditions.length > 0 && borderConditions.every(isBorderConditionValid),
    [borderConditions],
  );

  const handleBorderChange = useCallback(
    (changes: Partial<HubOption>) => onUpdate(option.uuid, changes),
    [onUpdate, option.uuid],
  );

  const {
    addCondition: addBorderOverride,
    removeCondition: removeBorderOverride,
    updateCondition: updateBorderOverride,
    handleDragEnd: handleBorderDragEnd,
    summary: borderSummary,
  } = useBorderConditions(borderConditions, option.borderColor ?? null, handleBorderChange);

  const handleBorderColorChange = useCallback(
    (next: string | null) => onUpdate(option.uuid, { borderColor: next }),
    [onUpdate, option.uuid],
  );

  const handleBorderColorClear = useCallback(
    () => onUpdate(option.uuid, { borderColor: null }),
    [onUpdate, option.uuid],
  );

  const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const iconId = e.target.value ? Number(e.target.value) : null;
    onUpdate(option.uuid, { iconId });
  }, [onUpdate, option.uuid]);

  const handleScoreChange = useCallback((score: HubOptionScoreType | null) => {
    onUpdate(option.uuid, { score });
  }, [onUpdate, option.uuid]);

  const optionStateMemo = useMemo(
    () => option.state
      ? { state: option.state.state as HubOptionStateValue, text: option.state.text }
      : DEFAULT_OPTION_STATE,
    [option.state],
  );

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const destinationLabel = getBranchName(option.gotoBranchId, branches);

  // Text & Dest overrides: dot visibility + validation (same pattern as State)
  const textDestConditions = useMemo(() => option.textDestConditions ?? [], [option.textDestConditions]);
  const hasTextDestOverrides = textDestConditions.length > 0;
  const textDestOverridesConfigured = useMemo(() => hasTextDestOverrides && textDestConditions.every((c) => {
    const subsValid = c.subConditions.length > 0 && c.subConditions.every((sub) => {
      if (sub.variableId == null) return false;
      const hasInt = sub.intValue !== undefined && sub.intValue !== '';
      const hasVar = sub.varValue !== undefined && sub.varValue !== null && sub.varValue !== 0;
      return hasInt || hasVar;
    });
    if (!subsValid) return false;
    return c.resultGotoBranchId != null;
  }), [hasTextDestOverrides, textDestConditions]);

  // Icon overrides: dot visibility + validation (same pattern as State)
  const iconConditions = useMemo(() => option.iconConditions ?? [], [option.iconConditions]);
  const hasIconOverrides = iconConditions.length > 0;
  const iconOverridesConfigured = useMemo(() => hasIconOverrides && iconConditions.every((c) => {
    const subsValid = c.subConditions.length > 0 && c.subConditions.every((sub) => {
      if (sub.variableId == null) return false;
      const hasInt = sub.intValue !== undefined && sub.intValue !== '';
      const hasVar = sub.varValue !== undefined && sub.varValue !== null && sub.varValue !== 0;
      return hasInt || hasVar;
    });
    if (!subsValid) return false;
    return c.resultIconId != null;
  }), [hasIconOverrides, iconConditions]);

  const score = option.score ?? null;

  const hasScore = useMemo(() => {
    if (!score) return false;
    const hasNum = score.numeratorType === 'literal'
      ? score.numeratorLiteral != null && score.numeratorLiteral !== ''
      : score.numeratorVariableId != null;
    const hasDen = score.denominatorType === 'literal'
      ? score.denominatorLiteral != null && score.denominatorLiteral !== ''
      : score.denominatorVariableId != null;
    return hasNum || hasDen;
  }, [score]);

  const scoreIsConfigured = useMemo(() => {
    if (!score) return true;
    const hasNum = score.numeratorType === 'literal'
      ? score.numeratorLiteral != null && score.numeratorLiteral !== ''
      : score.numeratorVariableId != null;
    const hasDen = score.denominatorType === 'literal'
      ? score.denominatorLiteral != null && score.denominatorLiteral !== ''
      : score.denominatorVariableId != null;
    if (!hasNum && !hasDen) return true;
    if (hasNum !== hasDen) return false;
    if (score.numeratorType === 'literal' && score.denominatorType === 'literal') {
      const num = Number(score.numeratorLiteral);
      const den = Number(score.denominatorLiteral);
      if (Number.isFinite(num) && Number.isFinite(den)) {
        if (den === 0) return false;
      }
    }
    return true;
  }, [score]);

  const textDestSummary = useMemo(() => {
    if (textDestConditions.length === 0) return undefined;
    const first = textDestConditions[0];
    const parts: string[] = [];
    if (first.resultText != null) parts.push(`"${first.resultText}"`);
    if (first.resultGotoBranchId != null) {
      const name = getBranchName(first.resultGotoBranchId, branches);
      parts.push(`→ ${name || '...'}`);
    }
    let text = parts.join(' ') || 'override';
    if (textDestConditions.length > 1) text += ` + ${textDestConditions.length - 1} more`;
    return text;
  }, [textDestConditions, branches]);

  const titleLength = option.title?.length ?? 0;
  const titleMaxLength = isContinue ? HUB_CONTINUE_MAX_LENGTH : HUB_CARD_TEXT_MAX_LENGTH;
  const titleWarnLength = isContinue ? HUB_CONTINUE_MAX_LENGTH : HUB_CARD_TEXT_WARN_LENGTH;

  const textDestControl = (
    <div className="text-dest-inline d-flex align-items-center">
      <label htmlFor={`hubOptionTitle-${option.uuid}`} className="sr-only">Title</label>
      <div className="text-dest-title-input">
        <input
          id={`hubOptionTitle-${option.uuid}`}
          type="text"
          className={`form-control form-control-sm${titleLength > titleWarnLength ? ' text-warn' : ''}`}
          value={option.title}
          maxLength={titleMaxLength}
          placeholder="Option title"
          disabled={isDisabled}
          onChange={(e) => onUpdate(option.uuid, { title: e.target.value })}
        />
        <small className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
          {titleWarnLength - titleLength}
        </small>
      </div>
      <CustomSelect
        disabled={isDisabled}
        value={option.gotoBranchId ?? ''}
        onChange={(raw: unknown) => onUpdate(option.uuid, { gotoBranchId: parseGotoBranchId(raw) })}
        args={[
          { id: null, title: '' },
          { id: -1, title: '[Create New Node]' },
          ...branches,
        ]}
        data={currentBranchId}
      />
    </div>
  );

  return (
    <div className={`card-item${isContinue ? ' card-item--continue' : ''}`} data-option-uuid={option.uuid}>
      {/* ── Collapsed header ── */}
      <div
        className="card-item-header"
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="d-flex align-items-center card-header-left">
          {!isContinue && (
            <input
              type="checkbox"
              className="card-select-check mr-1"
              checked={isSelected}
              disabled={isDisabled}
              onChange={() => onToggleSelect?.(option.uuid)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <span className="card-index">#{index + 1}</span>
          <span className="card-title-label">{option.title || '(untitled)'}</span>
          {destinationLabel && (
            <small className="text-muted ml-2">&rarr; {destinationLabel}</small>
          )}
          {(option.stateConditions?.length ?? 0) > 0 && (
            <span className="badge badge-warning ml-1">STATE</span>
          )}
          {option.replay?.state && option.replay.state !== HubOptionStateValue.Hidden && (
            <span className="badge badge-secondary ml-1">↺</span>
          )}
          {(option.textDestConditions?.length ?? 0) > 0 && (
            <span className="badge badge-info ml-1">TXT</span>
          )}
          {(option.iconConditions?.length ?? 0) > 0 && (
            <span className="badge badge-secondary ml-1">ICON</span>
          )}
          {!isContinue && topDefaultTags.map((tag: any, i: number) => {
            const tmpl = tagTemplates.find((t: any) => t.id === tag.tagTemplateId);
            return tmpl ? <HubOptionTagChip key={`top-${tag.tagTemplateId}`} template={tmpl} className="ml-1" /> : null;
          })}
          {!isContinue && isValidBorderHex(option.borderColor) && (
            <span
              className="ml-1 d-inline-block align-middle"
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                border: '1px solid #ccc',
                backgroundColor: option.borderColor as string,
              }}
              title={`Border ${option.borderColor}`}
            />
          )}
          {!isContinue && !isValidBorderHex(option.borderColor) && (option.borderConditions?.length ?? 0) > 0 && (
            <span
              className="ml-1 badge badge-light"
              title="Border overrides configured"
              style={{ fontSize: '0.65rem' }}
            >
              ◈
            </span>
          )}
        </div>
        <div className="d-flex align-items-center">
          {isContinue && (
            <span className="badge badge-info ml-2">PINNED LAST</span>
          )}
          {!isContinue && (
            <Button
              variant="outline-primary"
              size="sm"
              className="btn-duplicate ml-1"
              disabled={isDisabled || isMaxReached}
              title={isMaxReached ? `Maximum ${HUB_MAX_REGULAR_OPTIONS} options reached` : 'Duplicate'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDuplicate(option.uuid);
              }}
            >
              Duplicate
            </Button>
          )}
          {!isContinue && (
            <Button
              variant="outline-danger"
              size="sm"
              className="ml-1"
              disabled={isDisabled}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              title="Delete option"
            >
              &times;
            </Button>
          )}
        </div>
      </div>

      {/* ── Expanded body ── */}
      {isExpanded && (
        <div className="card-item-body">
          {/* BEHAVIOR group */}
          <div className="section-group-label">Behavior</div>
          <OptionSectionRow
            label="Text & Dest"
            inlineControl={textDestControl}
            summary={textDestSummary}
            isConfigured={textDestOverridesConfigured}
            showDot={hasTextDestOverrides}
            hasError={hasTextDestOverrides && !textDestOverridesConfigured}
            expandLabel="Overrides"
            isExpanded={isTextDestOverridesOpen}
            onExpandToggle={() => setIsTextDestOverridesOpen((prev) => !prev)}
          >
            <TextDestOverrides
              optionUuid={option.uuid}
              defaultTitle={option.title}
              defaultGotoBranchId={option.gotoBranchId}
              textDestConditions={option.textDestConditions ?? []}
              branches={branches}
              currentBranchId={currentBranchId}
              isDisabled={isDisabled}
              isContinue={isContinue}
              onChange={(changes) => onUpdate(option.uuid, changes)}
            />
          </OptionSectionRow>
          <HubOptionState
            optionUuid={option.uuid}
            optionState={optionStateMemo}
            stateConditions={option.stateConditions ?? []}
            isDisabled={isDisabled}
            isContinue={isContinue}
            onChange={(changes) => onUpdate(option.uuid, changes)}
          />
          {!isContinue && (
            <HubOptionReplay
              optionUuid={option.uuid}
              replay={option.replay ?? null}
              replayConditions={option.replayConditions ?? []}
              branches={branches}
              currentBranchId={currentBranchId}
              isDisabled={isDisabled}
              onChange={(changes) => onUpdate(option.uuid, changes)}
            />
          )}

          {/* APPEARANCE group */}
          <div className="section-group-label mt-3">Appearance</div>
          <OptionSectionRow
            label="Icon"
            isConfigured={iconOverridesConfigured}
            showDot={hasIconOverrides}
            hasError={hasIconOverrides && !iconOverridesConfigured}
            summary={option.iconId != null
              ? `default: ${memoryIcons.find((i) => i.id === option.iconId)?.label ?? 'unknown'}`
              : 'no icon'}
            inlineControl={(
              <div className="icon-field-inline d-flex align-items-center">
                <MemoryIconField
                  id={`hubOptionIcon-${option.uuid}`}
                  label=""
                  memoryIcons={memoryIcons}
                  onSave={createMemoryIcon}
                  value={option.iconId != null ? String(option.iconId) : ''}
                  onChange={handleIconChange}
                  disabled={isDisabled}
                  variant="outline-primary"
                />
                {option.iconId != null && (
                  <HubClearButton
                    className="ml-1"
                    onClick={() => onUpdate(option.uuid, { iconId: null })}
                    disabled={isDisabled}
                    title="Clear icon"
                  />
                )}
              </div>
            )}
            expandLabel="Overrides"
            isExpanded={isIconOverridesOpen}
            onExpandToggle={() => setIsIconOverridesOpen((prev) => !prev)}
          >
            <IconOverrides
              optionUuid={option.uuid}
              iconConditions={option.iconConditions ?? []}
              isDisabled={isDisabled}
              onChange={(changes) => onUpdate(option.uuid, changes)}
            />
          </OptionSectionRow>
          {!isContinue && (
            <OptionSectionRow
              label="Score"
              isConfigured={scoreIsConfigured}
              showDot={hasScore}
              hasError={hasScore && !scoreIsConfigured}
              inlineControl={(
                <HubOptionScore
                  score={option.score ?? null}
                  isDisabled={isDisabled}
                  onChange={handleScoreChange}
                />
              )}
            />
          )}
          {!isContinue && (
            <OptionSectionRow
              label="Top Tags"
              isConfigured={topTagOverridesConfigured}
              showDot={hasTopTagOverrides}
              hasError={hasTopTagOverrides && !topTagOverridesConfigured}
              inlineControl={(
                <TagDefaultsPicker
                  tags={topDefaultTags}
                  templates={tagTemplates}
                  isDisabled={isDisabled}
                  onChange={(newTags) => onUpdate(option.uuid, { tags: { ...(option.tags ?? { top: [], bottom: [] }), top: newTags } })}
                />
              )}
              summary={topTagSummary}
              expandLabel={tagTemplates.length > 0 ? 'Overrides' : undefined}
              isExpanded={isTopTagsOpen}
              onExpandToggle={tagTemplates.length > 0 ? () => setIsTopTagsOpen((prev) => !prev) : undefined}
            >
              <TagsOverrides
                optionUuid={option.uuid}
                conditions={topTagConditions}
                templates={tagTemplates}
                isDisabled={isDisabled}
                onDragEnd={handleTopTagDragEnd}
                onAdd={addTopTagOverride}
                onRemove={removeTopTagOverride}
                onUpdate={updateTopTagOverride}
              />
            </OptionSectionRow>
          )}
          {!isContinue && (
            <OptionSectionRow
              label="Bottom Tags"
              isConfigured={bottomTagOverridesConfigured}
              showDot={hasBottomTagOverrides}
              hasError={hasBottomTagOverrides && !bottomTagOverridesConfigured}
              inlineControl={(
                <TagDefaultsPicker
                  tags={bottomDefaultTags}
                  templates={tagTemplates}
                  isDisabled={isDisabled}
                  onChange={(newTags) => onUpdate(option.uuid, { tags: { ...(option.tags ?? { top: [], bottom: [] }), bottom: newTags } })}
                />
              )}
              summary={bottomTagSummary}
              expandLabel={tagTemplates.length > 0 ? 'Overrides' : undefined}
              isExpanded={isBottomTagsOpen}
              onExpandToggle={tagTemplates.length > 0 ? () => setIsBottomTagsOpen((prev) => !prev) : undefined}
            >
              <TagsOverrides
                optionUuid={`${option.uuid}-bottom`}
                conditions={bottomTagConditions}
                templates={tagTemplates}
                isDisabled={isDisabled}
                onDragEnd={handleBottomTagDragEnd}
                onAdd={addBottomTagOverride}
                onRemove={removeBottomTagOverride}
                onUpdate={updateBottomTagOverride}
              />
            </OptionSectionRow>
          )}
          {!isContinue && (
            <OptionSectionRow
              label="Border"
              isConfigured={borderOverridesConfigured}
              showDot={hasBorderOverrides}
              hasError={hasBorderOverrides && !borderOverridesConfigured}
              summary={borderSummary}
              inlineControl={(
                <div className="d-flex align-items-center">
                  <BorderColorInput
                    value={option.borderColor}
                    onChange={handleBorderColorChange}
                    disabled={isDisabled}
                    testIdPrefix={`border-default-${option.uuid}`}
                  />
                  {option.borderColor != null && (
                    <HubClearButton
                      className="ml-1"
                      onClick={handleBorderColorClear}
                      disabled={isDisabled}
                      title="Clear border color"
                    />
                  )}
                </div>
              )}
              expandLabel="Overrides"
              isExpanded={isBorderOpen}
              onExpandToggle={() => setIsBorderOpen((prev) => !prev)}
            >
              <BorderOverrides
                optionUuid={option.uuid}
                conditions={borderConditions}
                isDisabled={isDisabled}
                onDragEnd={handleBorderDragEnd}
                onAdd={addBorderOverride}
                onRemove={removeBorderOverride}
                onUpdate={updateBorderOverride}
              />
            </OptionSectionRow>
          )}
        </div>
      )}

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        title="Delete Hub Option"
        confirmButtonTitle="Delete"
        isLoading={false}
        onConfirm={() => {
          onDelete([option.uuid]);
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      >
        Are you sure you want to delete this hub option?
      </ConfirmModal>
    </div>
  );
}
